'use client';

/**
 * /submit — project intake with two honest paths, Industry design system.
 *
 * The page used to lead every visitor into the expression-of-interest
 * mailbox while the self-serve application sat behind "Sign in". The
 * hierarchy is now inverted: the primary path creates an account and goes
 * straight into the five-step application in the portal; the expression of
 * interest stays as the secondary path for developers who want the
 * programme's guidance before drafting a PDD. The EOI form still posts to
 * /api/contact (rate-limited, same-origin checked, notifies the programme
 * by e-mail and receipts the sender).
 */
import Link from 'next/link';
import { useState } from 'react';
import { Blueprint, Kicker } from '@/components/ds/industry';

const ECOSYSTEMS = ['Mangrove', 'Seagrass', 'Saltmarsh', 'Reef / MPA', 'Pollution prevention', 'Other'];

export default function SubmitPage() {
  const [eco, setEco] = useState(ECOSYSTEMS[0]);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const projectName = String(fd.get('projectName') ?? '').trim();
    const organisation = String(fd.get('organisation') ?? '').trim();
    const email = String(fd.get('email') ?? '').trim();
    const country = String(fd.get('country') ?? '').trim();
    const area = String(fd.get('area') ?? '').trim();
    const website = String(fd.get('website') ?? '').trim(); // honeypot
    // The field says hectares and promised a number; it was type="text" with
    // inputMode="numeric", which is a keyboard hint and not a constraint, so it
    // accepted "dbbXKWRKGcUBKkdctg" from a bot on 5 September 2026.
    if (area && !/^[0-9]{1,9}([ ,.][0-9]{1,3})*$/.test(area)) {
      setState('error');
      return;
    }
    const summary = String(fd.get('summary') ?? '').trim();

    setState('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          website,
          name: organisation || projectName,
          email,
          subject: `Project expression of interest — ${projectName}`,
          message: [
            `Project: ${projectName}`,
            `Organisation: ${organisation}`,
            `Host country: ${country}`,
            `Approximate area (ha): ${area || '—'}`,
            `Ecosystem: ${eco}`,
            '',
            summary,
          ].join('\n'),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setState('sent');
      form.reset();
      setEco(ECOSYSTEMS[0]);
    } catch {
      setState('error');
    }
  };

  return (
    <main className="ind-container">
      {/* Hero */}
      <section style={{ padding: '72px 0 48px' }}>
        <Kicker>Project intake</Kicker>
        <h1 className="ind-h1">
          Submit a project
        </h1>
        <p style={{ fontSize: 15, lineHeight: '24px', maxWidth: '64ch', margin: '16px 0 0', color: 'var(--color-text)' }}>
          Two ways in. Ready to apply? Create an account and start the application directly — the register lists
          your project from submission onward. Not sure the project fits? Send an expression of interest first and
          the programme team responds with the applicable methodology family and next steps, typically within
          four weeks.
        </p>
      </section>

      {/* Two paths */}
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'clamp(20px, 3vw, 32px)', padding: '0 0 56px' }}>
        <Blueprint>
          <div style={{ padding: 'clamp(20px, 3vw, 32px)', display: 'flex', flexDirection: 'column', gap: 14, height: '100%', boxSizing: 'border-box' }}>
            <span className="tag tag-accent" style={{ alignSelf: 'flex-start' }}>Ready to apply</span>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 24, lineHeight: '28px', letterSpacing: '0.02em', textTransform: 'uppercase', margin: 0 }}>
              Start your application
            </h2>
            <p style={{ fontSize: 15, lineHeight: '24px', margin: 0, color: 'var(--color-text)' }}>
              Create an account, and the five-step application takes you through methodology, location and boundary,
              and the Project Design Document — with the adopted PDD template linked where you need it. On submission
              you receive an automated completeness report, and the review workflow of MCC-100 takes over.
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 'auto' }}>
              <Link href="/auth/register" className="btn btn-primary">Create an account &amp; start</Link>
              <Link href="/auth/login" style={{ fontSize: 14, color: 'var(--color-accent-700)', textDecoration: 'none', fontWeight: 600 }}>
                Already registered? Sign in
              </Link>
            </div>
          </div>
        </Blueprint>
        <Blueprint>
          <div style={{ padding: 'clamp(20px, 3vw, 32px)', display: 'flex', flexDirection: 'column', gap: 14, height: '100%', boxSizing: 'border-box' }}>
            <span className="tag tag-outline" style={{ alignSelf: 'flex-start' }}>Not sure it fits?</span>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 24, lineHeight: '28px', letterSpacing: '0.02em', textTransform: 'uppercase', margin: 0 }}>
              Talk to the programme first
            </h2>
            <p style={{ fontSize: 15, lineHeight: '24px', margin: 0, color: 'var(--color-text)' }}>
              A Project Design Document is real work. If you want the programme&rsquo;s view before you start — on
              eligibility, the right methodology family, or the host-country situation — send an expression of
              interest below and get a written answer before you invest in drafting.
            </p>
            <div style={{ marginTop: 'auto' }}>
              <a href="#eoi" className="btn btn-ghost">Send an expression of interest</a>
            </div>
          </div>
        </Blueprint>
      </section>

      {/* Form + aside */}
      <section id="eoi" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'clamp(24px, 5vw, 72px)', alignItems: 'start', padding: '0 0 72px', scrollMarginTop: 96 }}>
        <Blueprint>
          <form onSubmit={onSubmit} style={{ display: 'grid', gap: 20, padding: 'clamp(20px, 3vw, 32px)' }}>
            <div className="field">
              <label htmlFor="s-name">Project name</label>
              <input className="input" id="s-name" name="projectName" type="text" placeholder="e.g. Bay of Fundy Saltmarsh Restoration" required />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
              <div className="field">
                <label htmlFor="s-org">Organisation</label>
                <input className="input" id="s-org" name="organisation" type="text" placeholder="Developer, NGO or agency" required />
              </div>
              <div className="field">
                <label htmlFor="s-email">Contact email</label>
                <input className="input" id="s-email" name="email" type="email" placeholder="you@organisation.org" required />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
              <div className="field">
                <label htmlFor="s-country">Host country</label>
                <input className="input" id="s-country" name="country" type="text" placeholder="e.g. Tunisia" required />
              </div>
              <div className="field">
                <label htmlFor="s-area">Approximate area (ha)</label>
                <input className="input" id="s-area" name="area" type="text" inputMode="numeric" pattern="[0-9]{1,9}([ ,.][0-9]{1,3})*" title="A number of hectares, e.g. 2,400" placeholder="e.g. 2,400" />
              </div>
            </div>
            <div className="field">
              <label id="s-eco-label">Ecosystem</label>
              <div className="seg" role="radiogroup" aria-labelledby="s-eco-label">
                {ECOSYSTEMS.map((option) => (
                  <label key={option} className="seg-opt">
                    <input type="radio" name="eco" checked={eco === option} onChange={() => setEco(option)} />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="field">
              <label htmlFor="s-desc">Project summary</label>
              <textarea className="input" id="s-desc" name="summary" rows={5} placeholder="Site, activity, tenure and community context, expected outcomes" required />
            </div>
            {state === 'sent' && (
              <p role="status" style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--color-accent-700)' }}>
                Received. The programme team will respond by mail, typically within four weeks.
              </p>
            )}
            {state === 'error' && (
              <p role="status" style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--mcc-danger-fg)' }}>
                The submission could not be sent just now — please try again, or write to info@marineconservationcredits.com.
              </p>
            )}
            <div>
              {/* Honeypot: off-screen, not reachable by keyboard, ignored by
                  screen readers. Humans never fill it; generic form bots fill
                  everything. A filled value quarantines the submission server-side. */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
                <label htmlFor="s-website">Leave this field empty</label>
                <input id="s-website" name="website" type="text" tabIndex={-1} autoComplete="off" />
              </div>
                            <button type="submit" className="btn btn-primary" disabled={state === 'sending'}>
                {state === 'sending' ? 'Sending…' : 'Submit expression of interest'}
              </button>
            </div>
          </form>
        </Blueprint>

        <aside>
          <Kicker ruleMargin="0 0 24px">Before you submit</Kicker>
          <div style={{ display: 'grid', gap: 28 }}>
            <div>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 20, lineHeight: '24px', letterSpacing: '0.02em', textTransform: 'uppercase', margin: 0 }}>Eligibility</h3>
              <p style={{ fontSize: 15, lineHeight: '24px', margin: '8px 0 0', color: 'var(--color-text)' }}>
                Marine or coastal ecosystems within a methodology family (F01–F05), with demonstrable tenure or
                management rights and host-country legality.
              </p>
            </div>
            <div>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 20, lineHeight: '24px', letterSpacing: '0.02em', textTransform: 'uppercase', margin: 0 }}>Safeguards</h3>
              <p style={{ fontSize: 15, lineHeight: '24px', margin: '8px 0 0', color: 'var(--color-text)' }}>
                FPIC, indigenous rights and benefit-sharing arrangements are screened at validation. Start community
                engagement before drafting the PDD.
              </p>
            </div>
            <div>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 20, lineHeight: '24px', letterSpacing: '0.02em', textTransform: 'uppercase', margin: 0 }}>What happens next</h3>
              <p style={{ fontSize: 15, lineHeight: '24px', margin: '8px 0 0', color: 'var(--color-text)' }}>
                Screening → methodology assignment → PDD drafting → independent validation → registration. The full
                pathway is described in <Link href="/methodologies" style={{ color: 'var(--color-accent-700)' }}>the standards</Link>.
              </p>
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
}
